import axios from "axios";
import { removeLocalStorageSkedApiToken } from "libs/storage/adapters";

import { getCSRFToken } from "./_api";

export type BetterAuthResource = "api" | "insights" | "inbox" | "link";

interface ResourceTokenResponse {
  accessToken: string;
  expiresIn: number;
  scope: string;
}

interface CachedResourceToken {
  token: string;
  expiresAt: number;
}

export type ResourceAuthMode = "unknown" | "better-auth" | "legacy";

const minimumValidityMs = 30_000;
const tokenCache = new Map<BetterAuthResource, CachedResourceToken>();
const pendingTokens = new Map<BetterAuthResource, Promise<string>>();
let authMode: ResourceAuthMode = "unknown";
let authModePromise: Promise<ResourceAuthMode> | undefined;
let generation = 0;

const selectBetterAuthMode = (): "better-auth" => {
  if (authMode !== "better-auth") {
    // A compatibility JWT may remain from the legacy session that performed
    // the JIT upgrade. Remove it as soon as Better Auth becomes authoritative
    // so no later consumer can resurrect it after a session/network failure.
    removeLocalStorageSkedApiToken();
  }
  authMode = "better-auth";
  return authMode;
};

export const getResourceAuthMode = (): ResourceAuthMode => authMode;

export const getCachedResourceToken = (
  resource: BetterAuthResource
): string | undefined => {
  const cached = tokenCache.get(resource);
  if (!cached) return undefined;
  if (cached.expiresAt <= Date.now() + minimumValidityMs) {
    tokenCache.delete(resource);
    return undefined;
  }
  return cached.token;
};

export const clearResourceToken = (resource?: BetterAuthResource): void => {
  generation += 1;
  if (resource) {
    tokenCache.delete(resource);
    pendingTokens.delete(resource);
    return;
  }
  tokenCache.clear();
  pendingTokens.clear();
  authMode = "unknown";
  authModePromise = undefined;
};

export const resolveResourceAuthMode = async (): Promise<ResourceAuthMode> => {
  if (authMode !== "unknown") return authMode;
  if (authModePromise) return authModePromise;
  const requestedGeneration = generation;
  const promise = axios
    .get<{ authenticated?: boolean }>("/auth/sked-auth/session", {
      // Invalid Better Auth sessions still use 401 and must fail closed instead
      // of silently downgrading to legacy authentication.
      validateStatus: () => true,
    })
    .then((response) => {
      if (requestedGeneration !== generation)
        throw new Error("Authentication context changed");
      if (response.status === 200 && response.data?.authenticated === true) {
        return selectBetterAuthMode();
      }
      if (
        response.status === 200 &&
        response.data?.authenticated === false &&
        Object.keys(response.data).length === 1
      ) {
        authMode = "legacy";
        return authMode;
      }
      throw new Error(
        `Unable to determine the browser authentication mode (${response.status})`
      );
    })
    .finally(() => {
      if (authModePromise === promise) authModePromise = undefined;
    });
  authModePromise = promise;
  return authModePromise;
};

export const getResourceTokenIfAvailable = async (
  resource: BetterAuthResource,
  force = false
): Promise<string | undefined> => {
  if ((await resolveResourceAuthMode()) === "legacy") return undefined;
  return requestResourceToken(resource, force);
};

export const getResourceToken = async (
  resource: BetterAuthResource,
  force = false
): Promise<string> => {
  if ((await resolveResourceAuthMode()) !== "better-auth") {
    throw new Error("A Better Auth session is required for resource tokens");
  }
  return requestResourceToken(resource, force);
};

const requestResourceToken = async (
  resource: BetterAuthResource,
  force: boolean
): Promise<string> => {
  const requestedGeneration = generation;
  const cached = tokenCache.get(resource);
  if (!force && cached && cached.expiresAt > Date.now() + minimumValidityMs) {
    return cached.token;
  }

  const pending = pendingTokens.get(resource);
  if (pending) return pending;

  const promise = getCSRFToken()
    .then((csrfToken) =>
      axios.post<ResourceTokenResponse>(
        "/auth/sked-auth/resource-token",
        { resource },
        { headers: { "X-XSRF-TOKEN": csrfToken } }
      )
    )
    .then(({ data }) => {
      if (requestedGeneration !== generation)
        throw new Error("Authentication context changed");
      selectBetterAuthMode();
      tokenCache.set(resource, {
        token: data.accessToken,
        expiresAt: Date.now() + data.expiresIn * 1000,
      });
      return data.accessToken;
    })
    .finally(() => {
      if (pendingTokens.get(resource) === promise)
        pendingTokens.delete(resource);
    });
  pendingTokens.set(resource, promise);
  return promise;
};
